import { Dtos, SuretyAccountId, SuretyQuoteId } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createVersionConflictHandler } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { accountQueryKey } from "../../account/account.keys";
import { bondRequestPreviewsQueryKey, bondRequestsQueryKey } from "../bond-request.keys";
import { invalidateBondRequestIfClosed, redirectToIssuedBond } from "../utils";

export const useUpdateBondRequest = (
	id: SuretyQuoteId,
	accountId: SuretyAccountId,
	options?: { onConflict?: () => void },
) => {
	const queryClient = useQueryClient();
	const { patch } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const queryKeys = [
		bondRequestsQueryKey(id),
		bondRequestPreviewsQueryKey(),
		accountQueryKey(accountId),
	];

	const mutation = useMutation({
		meta: { redirectOnError: redirectToIssuedBond },
		mutationKey: ["updateBondRequest", id],
		mutationFn: async ({
			data,
		}: {
			data: Dtos.SuretyQuote.Update.Request;
			onSuccess?: () => void;
		}) => {
			await patch(`/v2/surety/accounts/${accountId}/quotes/${id}`, data, "none");
		},
		onError: createVersionConflictHandler(queryKeys, queryClient, triggerErrorModal, {
			refetchType: "active",
			onConflict: options?.onConflict,
		}),
		onSettled: invalidateBondRequestIfClosed(queryClient, id),
		onSuccess: async (_, { onSuccess }) => {
			queryKeys.forEach((queryKey) => {
				queryClient.invalidateQueries({ queryKey });
			});

			onSuccess && onSuccess();
		},
	});

	return {
		updateBondRequest: mutation.mutate,
		updateBondRequestIsLoading: mutation.isPending,
		updateBondRequestError: mutation.error,
	};
};
